import expensesOnboardingCard3 from 'assets/images/expensesOnboardingCard3.png';
import repositoryOnboardingImg1 from 'assets/images/repository-onboarding-1.png';
import repositoryOnboardingImg3 from 'assets/images/repository-onboarding-3.png';
import { Modal } from 'components/atoms/Modal';

import createRepoDocument from 'api/v2/repository/createRepoDocument';
import { uploadFile } from 'api/v2/upload';
import OnboardingCardsCarousel from 'components/molecules/OnboardingCardsCarousel';
import { FileUploader } from 'components/organisms/FileUploader';
import layoutContext from 'components/organisms/Layout/layoutContext';
import type { FC } from 'react';
import { useContext, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { DocumentsRepositoryCacheKeys } from 'types/cacheKeys.types';
import { UploadTypesEnum } from 'types/uploadEnums.types';
import prefetchOnboardingImages from 'utils/prefetchOnboardingImages';
import showToast from 'utils/toast';
import {
  DropzoneContent,
  FilesListFooter,
  FilesListHeader,
} from './fileUploader.content';

const cards = [
  {
    img: repositoryOnboardingImg1,
    title: 'repository.onboarding.card1.title',
    description: 'repository.onboarding.card1.subtitle',
    btnText: 'continue',
  },
  {
    img: expensesOnboardingCard3,
    title: 'repository.onboarding.card2.title',
    description: 'repository.onboarding.card2.subtitle',
    btnText: 'continue',
  },
  {
    img: repositoryOnboardingImg3,
    title: 'repository.onboarding.card3.title',
    description: 'repository.onboarding.card3.subtitle',
    btnText: 'continue',
  },
];

const DocumentsOnboarding: FC = () => {
  const queryClient = useQueryClient();

  prefetchOnboardingImages();

  const { t } = useTranslation();

  const layout = useContext(layoutContext);

  useLayoutEffect(() => {
    layout.setShouldShowBgImage(false);
    return () => {
      layout.setShouldShowBgImage(layout.shouldShowBgImage);
    };
  }, []);

  return (
    <div>
      <div className="text-center">
        <h1 className="font-avenir text-5xl font-bold text-primary-700 leading-tight">
          {t('repository.onboarding.title')}
        </h1>

        <p className="text-primary-800 text-center text-lg mt-2 w-[50%] mx-auto mb-16">
          {t('repository.onboarding.subtitle')}
        </p>
      </div>

      <div className="flex gap-4 max-w-6xl m-auto">
        <FileUploader
          className="flex-1 h-auto flex-col lg:flex-row"
          keepUploadingAfterDestroy
          onUpload={(api) => {
            return uploadFile({
              file: api.file,
              category: UploadTypesEnum.documentsRepository,
            }).then(({ key }: any) =>
              createRepoDocument({
                filename: key.split('/').pop(),
                name: api.file.name,
              }).then(() => {
                queryClient.setQueryData(
                  DocumentsRepositoryCacheKeys.HasDocuments,
                  true,
                );

                queryClient.invalidateQueries(
                  DocumentsRepositoryCacheKeys.DocumentsList,
                );
              }),
            );
          }}
          afterPatchUpload={(fileUploaderMeta) => {
            if (fileUploaderMeta.done) {
              showToast(
                { title: t('repository.uploading_docs.success_msg') },
                { type: 'success' },
              );
            }
          }}
          onAfterDropAccepted={(processFiles) => processFiles()}
          dropzoneProps={{
            render: (dropzoneMeta, fileUploaderMeta) => (
              <DropzoneContent
                dropzoneMeta={dropzoneMeta}
                fileUploaderMeta={fileUploaderMeta}
              />
            ),
          }}
          filesListProps={{
            renderHeader: (fileUploaderMeta) => (
              <FilesListHeader fileUploaderMeta={fileUploaderMeta} />
            ),
            renderFooter: (fileUploaderMeta) => {
              return <FilesListFooter fileUploaderMeta={fileUploaderMeta} />;
            },
          }}
        />
      </div>

      <Modal
        name="reasons to add documents"
        width="2xl"
        trigger={(api) => (
          <div className="flex flex-col items-center">
            <p className="text-primary-800 text-center text-lg mt-16 w-[50%] mx-auto">
              {t('repository.onboarding.footer.title')}
            </p>

            <button
              type="button"
              onClick={api.openModal}
              className="mb-12 mt-1 text-[1.3rem] leading-tight text-xl text-primary hover:text-primary-800 cursor-pointer font-semibold underline"
            >
              {t('repository.onboarding.footer.cta')}
            </button>
          </div>
        )}
        render={(api) => (
          <OnboardingCardsCarousel cards={cards} onLast={api.closeModal} />
        )}
      />
    </div>
  );
};

export default DocumentsOnboarding;
